import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, message, Spin, Button } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyEmail } from './verify.service';

const { Title, Text } = Typography;

const Container = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  padding: 2rem;
  align-items: center;
  justify-content: center;
  background-color: #f4f6fb;
  flex-direction: column;
`;

const CardSection = styled.div`
  max-width: 440px;
  width: 100%;
  padding: 2.5rem;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const VerifyEmailPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleVerification = async () => {
      try {
        const token = searchParams.get('token');
        if (!token) {
          throw new Error('Invalid or missing verification token.');
        }

        const res = await verifyEmail(token);
        const msg = res.message || 'Email address verified successfully!';
        setSuccessMessage(msg);
        message.success(msg);

        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } catch (err: any) {
        const errMsg = err.response?.data?.message || err.message || 'Failed to verify email address.';
        setError(errMsg);
        message.error(errMsg);
      } finally {
        setLoading(false);
      }
    };

    handleVerification();
  }, [searchParams, navigate]);

  return (
    <Container>
      <CardSection>
        {loading ? (
          <>
            <Spin size="large" style={{ marginBottom: '1.5rem' }} />
            <Title level={4}>Verifying Email Address</Title>
            <Text type="secondary">Please wait while we confirm your email update...</Text>
          </>
        ) : error ? (
          <>
            <Title level={3} style={{ color: '#ff4d4f' }}>Verification Failed</Title>
            <Text type="danger" style={{ marginBottom: '1.5rem', display: 'block' }}>{error}</Text>
            <Button type="primary" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </>
        ) : (
          <>
            <Title level={3} style={{ color: '#52c41a' }}>Email Verified!</Title>
            <Text style={{ marginBottom: '1.5rem', display: 'block' }}>
              {successMessage}
            </Text>
            <Text type="secondary">Redirecting to your dashboard...</Text>
          </>
        )}
      </CardSection>
    </Container>
  );
};

export default VerifyEmailPage;
